import { Button } from '../ui/button'
// @ts-strict-ignore
import React, { useEffect, Fragment } from 'react'
import { MenuItem } from '../ui/menu'
import { Select, ItemRenderer, ItemPredicate } from '@blueprintjs/select'
import { useUser } from '../../hooks/useUser'
import { axiosPatch } from '../../util/api'
import ReactCountryFlag from 'react-country-flag'
import { useDispatch } from 'react-redux'
import { setCurrentUser } from '../../actions/user'
import { cents } from '../../util/formatters'
import { Toasts } from '../toaster/Toast'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import Profile = Models.Profile
import { Marketplaces } from '../../util/marketplaces'
import { type EntityId, idKey } from '../../types/constants'

interface DefaultProfileSelectProps {
    className?: ClassValue
    buttonProps?: object
    condensed?: boolean
    profileId?: EntityId
}

interface EnhancedProfileSelectProps extends DefaultProfileSelectProps {
    onChange: (Profile) => void
    selectedProfileId: EntityId | null
    syncDefaultIfMissing?: boolean

    filter?(profiles: Profile[]): Profile[]
}

// The saved default of the account: the default of the first settings row that names a profile
// the account holds. A row can have none, and a row can name a profile that the account holds
// no more: User::resolveProfileId and reducers/user.ts ignore that value too. With no such row
// the select shows and saves profiles[0]: the profile with the highest 30-day spend, because
// reducers/user.ts sorts the list by spend.
const savedDefaultProfile = (profiles: Profile[]): EntityId | undefined => {
    // Profile IDs can exceed 2^53: compare the keys, never the numbers.
    const held = new Set(profiles.map((profile) => idKey(profile.profile_id)))
    return profiles.find(
        (profile) =>
            profile.settings?.default_profile != null &&
            held.has(idKey(profile.settings.default_profile)),
    )?.settings?.default_profile
}

export const DefaultProfileSelect = ({
    className,
    buttonProps,
    condensed,
    profileId,
}: DefaultProfileSelectProps) => {
    const dispatch = useDispatch()
    const { profiles, settings } = useUser()

    const saveDefaultProfile = (selected: Profile, silent?: boolean) => {
        axiosPatch(`settings/${settings.id}/account`, {
            default_profile: selected.profile_id,
        }).then((res) => {
            dispatch(setCurrentUser(res.data))
            if (!silent) {
                Toasts.success('Default profile updated!')
            }
        })
    }

    return (
        <EnhancedProfileSelect
            condensed={condensed}
            onChange={(selected: Profile) => saveDefaultProfile(selected)}
            className={className}
            buttonProps={buttonProps}
            selectedProfileId={savedDefaultProfile(profiles) ?? profiles[0]?.profile_id}
            syncDefaultIfMissing
        />
    )
}

export const EnhancedProfileSelect = ({
    className,
    buttonProps,
    onChange,
    selectedProfileId,
    syncDefaultIfMissing = false,
    condensed,
    filter,
}: EnhancedProfileSelectProps) => {
    const dispatch = useDispatch()
    const { profiles, screenshotMode, settings } = useUser()
    const displayCount = condensed ? 20 : 5

    const selectedProfile = profiles.find(
        (profile) => idKey(profile.profile_id) === idKey(selectedProfileId),
    )

    const ProfileSelect = Select.ofType<Profile>()

    const saveDefaultProfile = (selected: Profile, silent?: boolean) => {
        axiosPatch(`settings/${settings.id}/account`, {
            default_profile: selected.profile_id,
        }).then((res) => {
            dispatch(setCurrentUser(res.data))
            if (!silent) {
                Toasts.success('Default profile updated!')
            }
        })
    }

    useEffect(() => {
        // If default profile is not selected, set the first profile as default
        if (syncDefaultIfMissing && !!settings && !savedDefaultProfile(profiles)) {
            const profile = profiles[0] as Profile
            saveDefaultProfile(profile, true)
        }
    }, [profiles, settings, syncDefaultIfMissing])

    const renderProfile: ItemRenderer<Profile> = (
        profile,
        { handleClick, modifiers, query, index },
    ) => {
        if (!modifiers.matchesPredicate) {
            return null
        }
        const text = (
            <div>
                <span>
                    {/*
                // @ts-ignore*/}
                    <ReactCountryFlag
                        countryCode={profile.country_code === 'UK' ? 'gb' : profile.country_code}
                        svg
                        className="mr-3 text-lg"
                    />
                    {profile.nickname ? profile.nickname : profile.name}
                    <br />
                    {!condensed && (
                        <Fragment>
                            {profile.nickname ? (
                                <Fragment>
                                    {profile.name}
                                    <br />
                                </Fragment>
                            ) : null}
                            {profile.profile_id}
                        </Fragment>
                    )}
                </span>
            </div>
        )

        if (index <= displayCount) {
            return (
                <MenuItem
                    className={classNames({ blur: screenshotMode })}
                    active={idKey(selectedProfile?.profile_id) === idKey(profile?.profile_id)}
                    disabled={modifiers.disabled}
                    label={`30D Ad Spend: ${(
                        cents(
                            profile.total_ad_spend_30_days,
                            Marketplaces[profile.marketplace_id],
                        ) ?? ''
                    ).toString()}`}
                    key={profile.profile_id}
                    onClick={handleClick}
                    text={text}
                />
            )
        } else if (index === displayCount + 1) {
            return (
                <MenuItem
                    disabled
                    text={`And ${profiles.length - displayCount - 1} more not shown`}
                />
            )
        }
    }

    const filterFilm: ItemPredicate<Profile> = (query, profile, _index, exactMatch): boolean => {
        const normalizedQuery = query.toLowerCase()
        const normalizedNickname = profile?.nickname?.toLowerCase()
        const normalizedName = profile?.name?.toLowerCase()

        return (
            (normalizedNickname && normalizedNickname.indexOf(normalizedQuery) >= 0) ||
            (normalizedName && normalizedName.indexOf(normalizedQuery) >= 0)
        )
    }

    if (!selectedProfileId) {
        return null
    }

    return (
        <div className={classNames(className)}>
            <ProfileSelect
                popoverProps={{
                    placement: 'bottom-start',
                    popoverClassName: 'rounded-lg profile-select-popover',
                }}
                itemsEqual="profile_id"
                items={filter ? filter(profiles) : profiles}
                itemPredicate={filterFilm}
                itemRenderer={renderProfile}
                noResults={<MenuItem disabled={true} text="No results." />}
                onItemSelect={onChange}
                inputProps={{
                    className: 'rounded-lg profile-select-input',
                }}
            >
                <Button
                    className={classNames({ blur: screenshotMode })}
                    text={selectedProfile?.nickname || selectedProfile?.name}
                    {...buttonProps}
                    rightIcon="caret-down"
                />
            </ProfileSelect>
        </div>
    )
}
